import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import {
  RefreshCw,
  Gem,
  TrendingUp,
  TrendingDown,
  Target,
  Zap,
  AlertTriangle,
  DollarSign,
  BarChart3,
  ShoppingCart,
  CheckCircle,
  XCircle,
  Activity,
  Percent,
  Users,
  Building2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

export default function HiddenGems() {
  const [gems, setGems] = useState(null);
  const [paperTrades, setPaperTrades] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [buyingSymbol, setBuyingSymbol] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const gemsRes = await api.get("/research/hidden-gems");
      setGems(gemsRes.data);
      
      // Paper trades require auth - fetch separately so gems still load
      try {
        const tradesRes = await api.get("/paper-trade/portfolio");
        setPaperTrades(tradesRes.data);
      } catch {
        setPaperTrades({ open_trades: [], closed_trades: [], total_open: 0 });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load hidden gems");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success("Hidden gems updated");
  };

  const handlePaperBuy = async (gem) => {
    setBuyingSymbol(gem.symbol);
    try {
      await api.post("/paper-trade/buy", {
        symbol: gem.symbol,
        entry_price: gem.current_price,
        target_price: gem.trade_setup?.target || gem.current_price * 1.3,
        stop_loss: gem.trade_setup?.stop_loss || gem.current_price * 0.85,
        algorithm: "hidden_gems",
        signals: gem.signals?.slice(0, 3) || [],
        confidence: gem.gem_score
      });
      toast.success(`Paper trade opened for ${gem.symbol}!`);
      await fetchData();
    } catch (error) {
      toast.error("Failed to create paper trade");
    } finally {
      setBuyingSymbol(null);
    }
  };

  const handleCloseTrade = async (symbol) => {
    try {
      const res = await api.post(`/paper-trade/close/${symbol}`);
      toast.success(`Trade closed: ${res.data.result} (${res.data.pnl_pct > 0 ? '+' : ''}${res.data.pnl_pct}%)`);
      await fetchData();
    } catch (error) {
      toast.error("Failed to close trade");
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const isAlreadyBought = (symbol) => {
    return paperTrades?.open_trades?.some(t => t.symbol === symbol);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-profit bg-profit/20";
    if (score >= 60) return "text-yellow-400 bg-yellow-400/20";
    if (score >= 40) return "text-orange-400 bg-orange-400/20";
    return "text-muted-foreground bg-muted";
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "momentum_plays": return <Zap className="w-3 h-3" />;
      case "deep_value": return <Gem className="w-3 h-3" />;
      case "breakout_setups": return <TrendingUp className="w-3 h-3" />;
      case "short_squeeze": return <AlertTriangle className="w-3 h-3" />;
      case "insider_signals": return <Users className="w-3 h-3" />;
      case "chart_patterns": return <BarChart3 className="w-3 h-3" />;
      default: return <Activity className="w-3 h-3" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="hidden-gems-loading">
        <div className="h-48 skeleton rounded-sm" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-64 skeleton rounded-sm" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hidden-gems-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight flex items-center gap-3">
            <Gem className="w-8 h-8 text-ai" />
            HIDDEN GEMS FINDER
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Deep algorithm discovery • Chart patterns • Volume anomalies • Squeeze candidates
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-sm">
            {gems?.stats?.gems_found || gems?.hidden_gems?.length || 0} gems found
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="gap-2"
            data-testid="refresh-gems-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Scan Market
          </Button>
        </div>
      </div>

      <Tabs defaultValue="gems" className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
          <TabsTrigger value="gems">
            <Gem className="w-4 h-4 mr-2" />
            Hidden Gems ({gems?.hidden_gems?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="categories">
            <BarChart3 className="w-4 h-4 mr-2" />
            By Category
          </TabsTrigger>
          <TabsTrigger value="paper">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Paper Trades ({paperTrades?.total_open || 0})
          </TabsTrigger>
        </TabsList>

        {/* All Gems Tab */}
        <TabsContent value="gems" className="mt-6">
          {gems?.is_loading ? (
            <Card className="card-dark border-ai/30">
              <CardContent className="p-8 text-center">
                <RefreshCw className="w-12 h-12 text-ai mx-auto mb-4 animate-spin" />
                <p className="text-lg font-heading">{gems?.message}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Scanning {gems?.total_scanned || 60}+ stocks for hidden opportunities...
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {gems?.hidden_gems?.map((gem, idx) => (
                <Card 
                  key={gem.symbol}
                  className={`card-dark animate-slide-in ${gem.gem_score >= 70 ? 'border-ai/50' : ''}`}
                  style={{ animationDelay: `${idx * 30}ms` }}
                  data-testid={`gem-${gem.symbol}`}
                >
                  <CardContent className="p-4">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-heading font-bold text-xl">{gem.symbol}</p>
                          {gem.gem_score >= 70 && <Gem className="w-4 h-4 text-ai" />}
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-1">{gem.name}</p>
                        <p className="text-xs text-muted-foreground">{gem.sector}</p>
                      </div>
                      <Badge className={getScoreColor(gem.gem_score)}>
                        Score: {gem.gem_score}
                      </Badge>
                    </div>

                    {/* Price & Trade Setup */}
                    <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                      <div className="p-2 bg-black/30 rounded">
                        <p className="text-xs text-muted-foreground">Entry</p>
                        <p className="font-data font-bold">${gem.current_price}</p>
                      </div>
                      <div className="p-2 bg-profit/20 rounded">
                        <p className="text-xs text-muted-foreground">Target</p>
                        <p className="font-data font-bold text-profit">
                          ${gem.trade_setup?.target || 'N/A'}
                        </p>
                      </div>
                      <div className="p-2 bg-loss/20 rounded">
                        <p className="text-xs text-muted-foreground">Stop</p>
                        <p className="font-data font-bold text-loss">
                          ${gem.trade_setup?.stop_loss || 'N/A'}
                        </p>
                      </div>
                    </div>

                    {/* Risk/Reward */}
                    {gem.trade_setup?.risk_reward && (
                      <div className="flex items-center justify-between mb-3 p-2 bg-ai/10 rounded">
                        <span className="text-xs">Risk/Reward</span>
                        <span className={`font-data font-bold ${gem.trade_setup.risk_reward >= 2 ? 'text-profit' : 'text-warning'}`}>
                          1:{gem.trade_setup.risk_reward}
                        </span>
                      </div>
                    )}

                    {/* Categories */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {gem.categories?.map((cat, i) => (
                        <Badge key={i} variant="outline" className="text-xs flex items-center gap-1">
                          {getCategoryIcon(cat)}
                          {cat.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>

                    {/* Signals */}
                    <div className="space-y-1 mb-3 max-h-24 overflow-y-auto">
                      {gem.signals?.slice(0, 4).map((signal, i) => (
                        <p key={i} className="text-xs text-muted-foreground">
                          {signal}
                        </p>
                      ))}
                    </div>

                    {/* Key Metrics */}
                    <div className="grid grid-cols-4 gap-1 mb-3 text-center text-xs">
                      <div className="p-1 bg-black/20 rounded">
                        <p className="text-muted-foreground">RSI</p>
                        <p className={`font-data ${gem.metrics?.rsi < 35 ? 'text-profit' : ''}`}>
                          {gem.metrics?.rsi || 'N/A'}
                        </p>
                      </div>
                      <div className="p-1 bg-black/20 rounded">
                        <p className="text-muted-foreground">Vol</p>
                        <p className={`font-data ${gem.metrics?.volume_ratio > 1.5 ? 'text-ai' : ''}`}>
                          {gem.metrics?.volume_ratio}x
                        </p>
                      </div>
                      <div className="p-1 bg-black/20 rounded">
                        <p className="text-muted-foreground">Short</p>
                        <p className={`font-data ${gem.metrics?.short_interest > 15 ? 'text-warning' : ''}`}>
                          {gem.metrics?.short_interest || 0}%
                        </p>
                      </div>
                      <div className="p-1 bg-black/20 rounded">
                        <p className="text-muted-foreground">Upside</p>
                        <p className={`font-data ${gem.metrics?.upside_pct > 30 ? 'text-profit' : ''}`}>
                          +{gem.metrics?.upside_pct || 0}%
                        </p>
                      </div>
                    </div>

                    {/* Paper Trade Button */}
                    <Button
                      className={`w-full ${isAlreadyBought(gem.symbol) ? 'bg-muted' : 'bg-ai hover:bg-ai/80'}`}
                      size="sm"
                      onClick={() => handlePaperBuy(gem)}
                      disabled={buyingSymbol === gem.symbol || isAlreadyBought(gem.symbol)}
                      data-testid={`buy-${gem.symbol}`}
                    >
                      {buyingSymbol === gem.symbol ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : isAlreadyBought(gem.symbol) ? (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      ) : (
                        <ShoppingCart className="w-4 h-4 mr-2" />
                      )}
                      {isAlreadyBought(gem.symbol) ? 'Already Tracking' : 'Paper Trade'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="mt-6 space-y-6">
          {Object.entries(gems?.categories || {}).map(([category, stocks]) => (
            stocks.length > 0 && (
              <div key={category}>
                <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2 capitalize">
                  {getCategoryIcon(category)}
                  {category.replace('_', ' ')} ({stocks.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {stocks.slice(0, 4).map((gem) => (
                    <Card key={gem.symbol} className="card-dark">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-heading font-bold">{gem.symbol}</span>
                          <Badge className={getScoreColor(gem.gem_score)} variant="outline">
                            {gem.gem_score}
                          </Badge>
                        </div>
                        <p className="text-sm font-data">${gem.current_price}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                          {gem.signals?.[0]}
                        </p>
                        <Button
                          className="w-full mt-2 bg-ai/80 hover:bg-ai"
                          size="sm"
                          onClick={() => handlePaperBuy(gem)}
                          disabled={isAlreadyBought(gem.symbol)}
                        >
                          {isAlreadyBought(gem.symbol) ? 'Tracking' : 'Paper Trade'}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )
          ))}
        </TabsContent>

        {/* Paper Trades Tab */}
        <TabsContent value="paper" className="mt-6 space-y-6">
          {/* Algorithm Performance */}
          {paperTrades?.algorithm_performance && Object.keys(paperTrades.algorithm_performance).length > 0 && (
            <Card className="card-dark border-ai/30">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-ai" />
                  ALGORITHM PERFORMANCE
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(paperTrades.algorithm_performance).map(([algo, stats]) => (
                    <div key={algo} className="p-4 bg-black/30 rounded">
                      <p className="font-heading text-sm uppercase mb-2">{algo.replace('_', ' ')}</p>
                      <div className="grid grid-cols-2 gap-2 text-center">
                        <div>
                          <p className="text-xs text-muted-foreground">Win Rate</p>
                          <p className={`font-data text-lg ${stats.win_rate >= 50 ? 'text-profit' : 'text-loss'}`}>
                            {stats.win_rate}%
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Avg P&L</p>
                          <p className={`font-data text-lg ${stats.avg_pnl >= 0 ? 'text-profit' : 'text-loss'}`}>
                            {stats.avg_pnl > 0 ? '+' : ''}{stats.avg_pnl}%
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground text-center mt-2">
                        {stats.wins}W / {stats.losses}L
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Open Trades */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2">
              <Activity className="w-5 h-5 text-warning" />
              OPEN POSITIONS ({paperTrades?.open_trades?.length || 0})
            </h3>
            {paperTrades?.open_trades?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {paperTrades.open_trades.map((trade) => (
                  <Card key={trade.symbol} className="card-dark">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-heading font-bold text-lg">{trade.symbol}</span>
                        <Badge className={trade.pnl_pct >= 0 ? 'bg-profit text-black' : 'bg-loss'}>
                          {trade.pnl_pct >= 0 ? '+' : ''}{trade.pnl_pct}%
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2 text-center text-sm mb-3">
                        <div>
                          <p className="text-xs text-muted-foreground">Entry</p>
                          <p className="font-data">${trade.entry_price}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Current</p>
                          <p className={`font-data ${trade.pnl_pct >= 0 ? 'text-profit' : 'text-loss'}`}>
                            ${trade.current_price}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Target</p>
                          <p className="font-data text-profit">${trade.target_price}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mb-3">
                        {trade.hit_target && (
                          <Badge className="bg-profit text-black">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Target Hit!
                          </Badge>
                        )}
                        {trade.hit_stop && (
                          <Badge className="bg-loss">
                            <XCircle className="w-3 h-3 mr-1" />
                            Stop Hit!
                          </Badge>
                        )}
                      </div>

                      <Button
                        className="w-full"
                        variant="outline"
                        size="sm"
                        onClick={() => handleCloseTrade(trade.symbol)}
                      >
                        Close Position
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="card-dark">
                <CardContent className="p-8 text-center">
                  <ShoppingCart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No open paper trades</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Use the "Paper Trade" button on any gem to start tracking
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Closed Trades */}
          {paperTrades?.closed_trades?.length > 0 && (
            <div>
              <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-muted-foreground" />
                CLOSED TRADES ({paperTrades.closed_trades.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {paperTrades.closed_trades.slice(0, 8).map((trade, idx) => (
                  <Card key={idx} className="card-dark">
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-heading font-bold">{trade.symbol}</span>
                        <Badge className={
                          trade.result === 'WIN' ? 'bg-profit text-black' :
                          trade.result === 'LOSS' ? 'bg-loss' : 'bg-muted'
                        }>
                          {trade.result}
                        </Badge>
                      </div>
                      <p className={`font-data text-lg ${trade.pnl_pct >= 0 ? 'text-profit' : 'text-loss'}`}>
                        {trade.pnl_pct >= 0 ? '+' : ''}{trade.pnl_pct}%
                      </p>
                      <p className="text-xs text-muted-foreground">
                        ${trade.entry_price} → ${trade.close_price}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Methodology */}
      <Card className="card-dark">
        <CardHeader>
          <CardTitle className="font-heading text-sm">DISCOVERY METHODOLOGY</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {gems?.methodology?.map((method, i) => (
              <Badge key={i} variant="outline" className="text-xs justify-start py-2">
                {method}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
